import toggleMarkdown, { renumberList } from "./editor/index";
import initMentions from "./editor/mentions";
import headlessTippy from "tippy.js/headless";
import ArticleSearch from "./alpine/article-search";
import Billing from "./alpine/billing";
import Tabs from "./alpine/tabs";
import BoundingBoxes from "./alpine/boundingBoxes";
import SearchSelect from "./alpine/search-select";
import ManualIssueForm from "./alpine/VisualTool/manual-issue-form";
import OnboardingTooltips from "./alpine/VisualTool/onboarding-tooltips";
import Chart from 'chart.js/auto';
import Chartist from 'chartist';
import { flare } from "@flareapp/flare-client";
import { startFlare } from './utils/flare-redact-secrets.js';
import ToastManager from "./alpine/toast-manager";
import { debounce } from "lodash";
import { getBrowserName } from "./utils.js"
import accordion from "./alpine/accordion.js";
import HtmlStyle from './alpine/html-style.js';
import { createHighlightSnippet } from './utils/element-targeting.js';
import { copyToClipboard, announceCopyStatus } from './utils/copy-to-clipboard.js';
import "../css/app.css";
import dirtyGuard from './alpine/directive/dirty-guard.js';
import { handleDomainPaste } from './alpine/strip-domain-prefix.js';
import revealed from './alpine/revealed.js';
import { STORE_CLICK_KEY, readStoreClick, recordStoreClick, clearStoreClick, promptVariant, storeClickFromOtherTab } from './alpine/extension-install-state.js';
import visualToolHandoff from './alpine/visual-tool-handoff.js';
import roundTripButton from './alpine/round-trip-button.js';
import './livewire-error-reporter.js';
import './session-expiry-watcher.js';
import './livewire-focus-guard.js';

document.addEventListener("DOMContentLoaded", function () {
    // Beta and production only. These reports go from the browser straight to Flare, so
    // startFlare() sets their filter before it starts the client.
    startFlare(flare, window.aaa_env);

    var skipLinkButton = document.getElementById('skip-to-support-link');

    setTimeout(function () {
        var skipLinkDestination = document.querySelector('#beacon-container');

        if (skipLinkDestination) {
            if (skipLinkButton) {
                // 'click' rather than Enter-only keydown: an anchor fires click for mouse,
                // Enter, voice control and screen-reader activation alike. Binding keydown
                // alone left everyone but Enter users with the raw fragment jump to a
                // non-focusable div, which drops focus instead of landing on the launcher.
                skipLinkButton.addEventListener('click', function (event) {
                    event.preventDefault();
                    handleSkipLink();
                });
            }
        }
    }, 2000);
});

// localStorage can throw on access (blocked site data, sandboxed frames).
function extensionStorage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

// PostHog only loads on production and beta, and not while impersonating
// (layouts/app.blade.php), so every client event is a no-op elsewhere.
function captureEvent(event, properties, options) {
    window.posthog?.capture?.(event, properties, options);
}

document.addEventListener('alpine:init', () => {

    // Resolves true or false and never rejects, so callers branch on the result
    // instead of claiming a copy that the browser refused.
    Alpine.magic('clipboard', () => subject => copyToClipboard(subject))

    // Speaks a copy result through the layout's #aaa-copy-status region.
    Alpine.magic('announceCopy', () => message => announceCopyStatus(message))

    Alpine.magic('handleDomainPaste', () => handleDomainPaste)

    // Every x-trap reads $revealed(...); see resources/js/alpine/revealed.js.
    Alpine.magic('revealed', revealed)

    Alpine.store('browserExtensionState', {
        enabled: false,
        extensionRefactorEnabled: false,

        // Install-prompt state. The prompt reads these through variant() to say
        // what it actually sees: not installed, mid-install, installed but still
        // not reaching this page, or ready. See alpine/extension-install-state.js.
        pageLoadedAt: Date.now(),
        storeClickedAt: null,
        detectionSettled: false,
        promptOpen: false,

        // What the user clicked when the install prompt opened, so "Open Visual
        // Mode" in the ready state continues to that page instead of starting over.
        pendingIntent: null,

        // The control that opened the prompt, so focus can go back to it (or to the
        // arm that replaced it) when the prompt closes.
        opener: null,

        // A Visual Mode entry has three outcomes: navigate to the in-app visual
        // tool, hand the URL to the extension, or ask the user to install it.
        // They used to be three separate elements in the markup, each x-show'd —
        // two dead nodes in every menu, on every row, on every site in the
        // sidebar. One element asks these two helpers at click time instead.
        visualModeHref(appUrl, siteUrl) {
            return this.enabled && this.extensionRefactorEnabled ? siteUrl : appUrl;
        },

        // KNOWN, ACCEPTED behaviour change from collapsing the three variants into
        // one <a>: a modifier click (Cmd/Ctrl-click, middle click, "Open in new
        // tab") never reaches a click handler's preventDefault(), so an
        // extension-less user who modifier-clicks Visual Mode opens the in-app
        // visual tool in a new tab instead of seeing the install modal. A plain
        // click still shows the modal. Before this change the no-extension variant
        // was a <button>, so a modifier click did nothing at all.
        //
        // Deliberately not "fixed": the in-app visual tool is a real, working
        // destination, and the server-rendered href has to stay a usable URL so the
        // link works before Alpine boots. Removing the href to force the modal would
        // trade a working new tab for a dead link during hydration.
        openVisualMode(event, appUrl, siteUrl, targetInstance = null) {
            this.detectFromMarker();

            if (!this.enabled) {
                event.preventDefault();
                this.opener = event.currentTarget ?? null;
                this.requestInstall(appUrl, siteUrl, targetInstance);
                return;
            }

            if (this.extensionRefactorEnabled) {
                event.preventDefault();
                this.postOpen(siteUrl, targetInstance);
            }

            // Otherwise let the anchor follow appUrl, the in-app visual tool.
        },

        // The same decision for the no-extension <button> arms, which have no href of
        // their own: re-check the marker, then install, hand off, or navigate.
        openFromButton(appUrl, siteUrl, targetInstance = null, opener = null) {
            this.detectFromMarker();

            if (!this.enabled) {
                this.opener = opener;
                this.requestInstall(appUrl, siteUrl, targetInstance);
                return;
            }

            this.pendingIntent = { appUrl, siteUrl, targetInstance };
            this.openPending();
        },

        // Opens the install prompt, remembering what the user was trying to open.
        // Every no-extension opener goes through here; UserProfessionModal opens the
        // prompt from the server with no intent, and the ready state then offers Done.
        requestInstall(appUrl = null, siteUrl = null, targetInstance = null) {
            this.pendingIntent = appUrl || siteUrl ? { appUrl, siteUrl, targetInstance } : null;
            window.Livewire.dispatch('setBrowserAddonRequiredModal', { data: { value: true } });
        },

        // The ready state's "Open Visual Mode": the same two outcomes openVisualMode
        // picks between once the extension is present.
        openPending() {
            const intent = this.pendingIntent;
            this.pendingIntent = null;
            if (!intent) {
                return;
            }

            if (this.extensionRefactorEnabled && intent.siteUrl) {
                this.postOpen(intent.siteUrl, intent.targetInstance);
                return;
            }

            if (intent.appUrl) {
                window.location.assign(intent.appUrl);
            }
        },

        // x-trap hands focus back to the opener when the prompt closes. The
        // no-extension arms of x-button.visual-mode and the first-scan card are
        // hidden once the extension is detected, so that hand-back lands on a hidden
        // element and focus falls to <body>. Called after the trap has had its turn:
        // if focus is still in the closed dialog or on <body>, move it to the opener,
        // or to the visible sibling arm with the same label.
        restoreFocusAfterPrompt(dialog) {
            const opener = this.opener;
            this.opener = null;
            if (!opener) {
                return;
            }

            const active = document.activeElement;
            const lost = !active || active === document.body || (dialog && dialog.contains(active));
            if (!lost) {
                return;
            }

            const label = opener.textContent.trim();
            const target = opener.offsetParent !== null
                ? opener
                : [...(opener.parentElement?.children ?? [])].find((el) => el !== opener
                    && el.matches('a, button')
                    && el.offsetParent !== null
                    && el.textContent.trim() === label);

            target?.focus();
        },

        postOpen(siteUrl, targetInstance = null) {
            window.postMessage({ type: 'AAARDVARK_OPEN_VISUAL_MODE', url: siteUrl, target_instance: targetInstance }, '*');
        },

        variant() {
            return promptVariant({
                enabled: this.enabled,
                storeClickedAt: this.storeClickedAt,
                pageLoadedAt: this.pageLoadedAt,
                detectionSettled: this.detectionSettled,
            });
        },

        noteStoreClick(source, browser) {
            captureEvent('extension_store_link_clicked', { source, browser, extension_detected: this.enabled });

            // A user who already has the extension is not mid-install; recording the
            // click would report a phantom install on their next page.
            if (this.enabled) {
                return;
            }

            const now = Date.now();
            recordStoreClick(extensionStorage(), now);
            this.storeClickedAt = now;
        },

        reloadForExtension(variant) {
            captureEvent('extension_install_reload_clicked', {
                variant,
                seconds_since_store_click: this.storeClickedAt === null
                    ? null
                    : Math.round((Date.now() - this.storeClickedAt) / 1000),
            }, { transport: 'sendBeacon' });
            window.location.reload();
        },

        // The extension appends this hidden span next to its one-shot
        // AAARDVARK_EXTENSION_ACTIVE message. The message is lost if it lands before
        // the detector's listener exists; the span stays, so it is the fallback.
        detectFromMarker() {
            if (this.enabled || !document.getElementById('aaardvark_extension_active')) {
                return false;
            }

            this.markDetected('marker');
            return true;
        },

        // One place flips detection on, so a double announce (the message and the
        // marker, or an extension injected twice) is counted once.
        markDetected(via) {
            if (this.enabled) {
                return;
            }

            this.enabled = true;

            if (via === 'marker') {
                captureEvent('extension_detected_via_marker', {});
            }

            const storage = extensionStorage();
            const clickedAt = readStoreClick(storage, Date.now());
            if (clickedAt !== null) {
                captureEvent('extension_detected_after_install', {
                    via,
                    modal_open: this.promptOpen,
                    seconds_since_store_click: Math.round((Date.now() - clickedAt) / 1000),
                });
                clearStoreClick(storage);
                this.storeClickedAt = null;
            }
        },

        // Alpine calls this once, when the store is registered.
        init() {
            this.storeClickedAt = readStoreClick(extensionStorage(), Date.now());

            window.addEventListener('aaardvarkExtensionActive', () => {
                this.markDetected('message');
            });

            // A store click in another AAArdvark tab counts here too: the install
            // belongs to the browser, not the tab. That tab clearing the record on
            // detection does not: see storeClickFromOtherTab().
            window.addEventListener('storage', (event) => {
                if (event.key === STORE_CLICK_KEY) {
                    this.storeClickedAt = storeClickFromOtherTab(this.storeClickedAt, event.newValue, Date.now());
                }
            });

            // PostHog feature flag for gradual rollout of extension visual mode.
            // Evaluated server-side and injected as window.AAA_FLAGS (see
            // layouts/app.blade.php) so production and beta behave the same;
            // localStorage is the dev override for environments without PostHog.
            let forced = false;
            try {
                forced = extensionStorage()?.getItem('force-extension-visual-mode') === 'true';
            } catch {
                // Storage blocked: no dev override.
            }
            this.extensionRefactorEnabled =
                window.AAA_FLAGS?.['extension-visual-mode'] === true || forced;
        }
    });

    // The desktop body of the Visual Mode install prompt
    // (livewire/site/browser-addon-required-modal.blade.php). Its body only exists
    // while the modal is open, so init() runs once per opening.
    Alpine.data('extensionInstallPrompt', (browser, statusMessages) => ({
        status: '',

        get variant() {
            return this.$store.browserExtensionState.variant();
        },

        // Read once, when the prompt opens: the openers set pendingIntent before the
        // round trip that renders this body. Reading it live flips the ready state's
        // "Open Visual Mode" to "Done" while the dialog fades out, because both
        // openPending() and the close handler below clear it at the start of the fade.
        hasIntent: false,

        init() {
            const store = this.$store.browserExtensionState;
            this.hasIntent = store.pendingIntent !== null;
            store.promptOpen = true;
            captureEvent('extension_install_prompt_shown', { variant: this.variant, browser });

            this.$watch('variant', (variant) => {
                captureEvent('extension_install_prompt_shown', { variant, browser });
                this.status = statusMessages[variant] ?? '';

                // The control the user was on (usually the store link they just
                // clicked) may have been hidden with its state; hand focus to the new
                // state's main action rather than dropping it on <body>. Focus
                // anywhere else in the dialog is left alone.
                //
                // "Needs moving" means on <body>, or still inside a state block that is
                // not the current one: the outgoing block can still be displayed when the
                // new state's action appears, so visibility alone reads as "still fine".
                // A control hidden outside the blocks (the Issues link hides on ready) can
                // keep focus in some browsers rather than dropping it to <body>.
                this.focusPrimaryWhenShown(() => {
                    const active = document.activeElement;
                    if (!active || active === document.body || active.offsetParent === null) {
                        return true;
                    }
                    const block = active.closest('[data-install-state]');
                    return Boolean(block) && this.$el.contains(block) && block.dataset.installState !== this.variant;
                });
            });

            // Focus on opening. x-trap cannot do it: it reads [autofocus] once, when the
            // always-mounted modal shell initialises on page load, before this body
            // exists, so it falls back to the first tabbable node (the help link in the
            // troubleshoot state). The trap records its return target when it activates,
            // two frames and 15ms after the modal shows (it waits on $revealed); focusing
            // after that leaves the opener as the return target. On a slow frame this
            // runs first, and the close handler below returns focus to the opener, when
            // the prompt has one.
            setTimeout(() => this.focusPrimaryWhenShown(() => true), 50);

            const dialog = this.$el.closest('[role=dialog]');
            this.$watch('show', (open) => {
                if (open) {
                    return;
                }

                store.promptOpen = false;
                store.pendingIntent = null;
                // After x-trap's own hand-back (a setTimeout of its own) has run.
                setTimeout(() => store.restoreFocusAfterPrompt(dialog), 50);
            });
        },

        // The current state's main action, once its block is visible. Scoped to the
        // state so the outgoing state's action, still visible for a frame, never counts.
        visiblePrimary() {
            return [...this.$el.querySelectorAll(`[data-install-state="${this.variant}"] [data-install-primary]`)]
                .find((el) => el.offsetParent !== null);
        },

        // A state's block becomes visible a frame or so after the store changes
        // (measured in Chrome: still hidden at $nextTick, setTimeout 0 and the first
        // animation frame), and the old block hides in the same step, which is when
        // the control the user was on loses focus. So wait, a frame at a time for up
        // to half a second, until the new state's main action is visible, and only
        // then ask whether focus needs moving.
        focusPrimaryWhenShown(shouldMove, framesLeft = 30) {
            const primary = this.visiblePrimary();
            if (!primary) {
                if (framesLeft > 0) {
                    requestAnimationFrame(() => this.focusPrimaryWhenShown(shouldMove, framesLeft - 1));
                }
                return;
            }

            if (shouldMove()) {
                primary.focus();
            }
        },

        reload() {
            this.$store.browserExtensionState.reloadForExtension(this.variant);
        },
    }));

    // The prompt's mobile and unsupported-browser arms have one control: the link to
    // the Issues list, or Close when the prompt was opened off a site page. They race
    // the same way: when x-trap activates before the deferred body arrives it finds
    // nothing to focus and leaves focus on the opener, behind the dialog (two of
    // fourteen opens in headless Chromium). So that control takes focus itself once
    // visible, unless focus is already inside. On a slow frame it does so before the
    // trap activates, and the trap records the control instead of the opener as where
    // to return focus, so the close hands focus back the way the install arm's does.
    Alpine.data('focusWhenShown', () => ({
        init() {
            const dialog = this.$el.closest('[role=dialog]');
            const attempt = (framesLeft) => {
                if (this.$el.offsetParent === null) {
                    if (framesLeft > 0) {
                        requestAnimationFrame(() => attempt(framesLeft - 1));
                    }
                    return;
                }

                if (!dialog?.contains(document.activeElement)) {
                    this.$el.focus();
                }
            };

            // 50ms in, as the install arm does.
            setTimeout(() => attempt(30), 50);

            const store = this.$store.browserExtensionState;
            this.$watch('show', (open) => {
                if (!open) {
                    // After x-trap's own hand-back (a setTimeout of its own) has run.
                    setTimeout(() => store.restoreFocusAfterPrompt(dialog), 50);
                }
            });
        },
    }));

    Alpine.data("ArticleSearch", ArticleSearch);
    Alpine.data("Billing", Billing);
    Alpine.data("SearchSelect", SearchSelect);
    Alpine.data("Tabs", Tabs);
    Alpine.data('Accordion', accordion);
    Alpine.data("BoundingBoxes", BoundingBoxes);
    Alpine.data("OnboardingTooltips", OnboardingTooltips);
    Alpine.data("ManualIssueForm", ManualIssueForm);
    Alpine.data("ToastManager", ToastManager);
    Alpine.data("HtmlStyle", HtmlStyle);
    Alpine.data('visualToolHandoff', visualToolHandoff);
    Alpine.data('roundTripButton', roundTripButton);

    Alpine.directive('dirty-guard', dirtyGuard);

    Alpine.store('toastStore', ToastManager());
})

window.toggleMarkdown = toggleMarkdown;
window.renumberList = renumberList;
window.initMentions = initMentions;
window.Chartist = Chartist;
window.Chart = Chart;
window.debounce = debounce;
window.getBrowserName = getBrowserName;

// Dark status-pill tooltip (matches the design's .pill-tooltip): gray-900 box,
// colored uppercase title, downward arrow. Portaled to <body> so it is never
// clipped by a card's overflow-hidden. Inline styles keep it safe from Tailwind
// JIT (this DOM is built in JS, not scanned from source).
window.tippy = function (selector, body, title = null, titleColor = "#FCA5A5") {
    if (!body || !body.length) {
        return;
    }
    headlessTippy(selector, {
        content: body,
        placement: "top",
        offset: [0, 10],
        // Stay above the pill (no flip) so the downward arrow always points at it.
        popperOptions: {
            modifiers: [{ name: "flip", options: { fallbackPlacements: [] } }],
        },
        render(instance) {
            const popper = document.createElement("div");
            popper.className = "tippy-box";

            const box = document.createElement("div");
            box.setAttribute("role", "tooltip");
            box.style.cssText =
                "position:relative;box-sizing:border-box;width:280px;max-width:280px;" +
                "padding:10px 12px 11px;border-radius:7px;background:#111827;color:#fff;" +
                "font-size:12px;font-weight:400;line-height:1.5;text-align:left;" +
                "box-shadow:0 12px 28px -8px rgba(0,0,0,0.4);border:1px solid rgba(255,255,255,0.08);";

            if (title) {
                const titleEl = document.createElement("span");
                titleEl.style.cssText =
                    "display:block;margin-bottom:4px;font-size:11px;font-weight:700;" +
                    "letter-spacing:0.06em;text-transform:uppercase;color:" + titleColor + ";";
                titleEl.textContent = title;
                box.appendChild(titleEl);
            }

            const bodyEl = document.createElement("div");
            bodyEl.textContent = instance.props.content;
            box.appendChild(bodyEl);

            const arrow = document.createElement("div");
            arrow.style.cssText =
                "position:absolute;left:50%;bottom:-6px;transform:translateX(-50%);" +
                "width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;" +
                "border-top:6px solid #111827;";
            box.appendChild(arrow);

            popper.appendChild(box);

            function onUpdate(prevProps, nextProps) {
                if (prevProps.content !== nextProps.content) {
                    bodyEl.textContent = nextProps.content;
                }
            }

            return {
                popper,
                onUpdate, // optional
            };
        },
    });
};

function handleSkipLink() {
    var iframeContainer = document.querySelector('#beacon-container');

    if (iframeContainer) {
        var iframe = iframeContainer.querySelector('iframe');

        if (iframe) {
            var helpChatButton = iframe.contentDocument.querySelector('button');

            if (helpChatButton) {
                helpChatButton.focus();
            }
        }
    }
}

// Make the element targeting utility available globally for the browser extension to use
window.aaardvarkUtils = {
    createHighlightSnippet
};
